import { Link } from "react-router-dom";
import { ExternalLink, Star, Terminal } from "lucide-react";
import { Button, Card, CardContent, cn, Tooltip, TooltipContent, TooltipTrigger } from "@fox1ck-org/ui";
import type { AppSummary } from "@/api/client";

interface AppCardProps {
  app: AppSummary;
  isFavorite?: boolean;
  onToggleFavorite?: (name: string) => void;
}

const HEALTH_DOT: Record<string, string> = {
  Healthy: "bg-emerald-500",
  Progressing: "bg-blue-500 animate-pulse",
  Degraded: "bg-red-500",
  Suspended: "bg-zinc-500",
  Missing: "bg-yellow-500",
  Unknown: "bg-zinc-500",
};

const SYNC_PILL: Record<string, string> = {
  Synced: "bg-emerald-500/15 text-emerald-400 border-emerald-500/30",
  OutOfSync: "bg-yellow-500/15 text-yellow-400 border-yellow-500/30",
  Unknown: "bg-zinc-500/15 text-zinc-400 border-zinc-500/30",
};

export function AppCard({ app, isFavorite = false, onToggleFavorite }: AppCardProps) {
  const dot = HEALTH_DOT[app.health.status] ?? HEALTH_DOT.Unknown!;
  const sync = SYNC_PILL[app.sync.status] ?? SYNC_PILL.Unknown!;
  const image = app.images[0]?.split("/").pop() ?? null;

  return (
    <Card
      className={cn(
        "group relative h-full transition-colors hover:border-primary/40",
        app.health.status === "Degraded" && "border-l-2 border-l-destructive",
        app.health.status === "Progressing" && "border-l-2 border-l-blue-500"
      )}
    >
      <Link to={`/apps/${app.name}`} className="block h-full">
        <CardContent className="p-3 space-y-2.5">
          <div className="flex items-start justify-between gap-2">
            <div className="flex items-center gap-2 min-w-0">
              <span
                className={cn("h-2 w-2 rounded-full shrink-0", dot)}
                aria-label={app.health.status}
              />
              <h3 className="text-sm font-medium text-foreground truncate">
                {app.name}
              </h3>
            </div>
            {onToggleFavorite && (
              <Tooltip>
                <TooltipTrigger asChild>
                  <button
                    type="button"
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      onToggleFavorite(app.name);
                    }}
                    className={cn(
                      "shrink-0 h-5 w-5 -m-0.5 rounded-sm flex items-center justify-center transition-colors",
                      isFavorite
                        ? "text-yellow-400"
                        : "text-muted-foreground/30 hover:text-yellow-400 opacity-0 group-hover:opacity-100"
                    )}
                    aria-label={isFavorite ? "Убрать из избранного" : "Добавить в избранное"}
                  >
                    <Star className={cn("h-3.5 w-3.5", isFavorite && "fill-current")} />
                  </button>
                </TooltipTrigger>
                <TooltipContent>{isFavorite ? "Убрать из избранного" : "В избранное"}</TooltipContent>
              </Tooltip>
            )}
          </div>

          <div className="flex items-center gap-1.5 flex-wrap">
            <span
              className={cn(
                "px-1.5 h-5 leading-5 rounded text-[10px] font-semibold uppercase border",
                sync
              )}
            >
              {app.sync.status}
            </span>
            <span className="text-[10px] text-muted-foreground">{app.namespace}</span>
          </div>

          {image && (
            <p
              className="text-[11px] text-muted-foreground font-mono truncate"
              title={app.images[0]}
            >
              {image}
            </p>
          )}

          <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity pt-1">
            <Button asChild size="sm" variant="ghost" className="h-6 px-1.5 text-[10px]">
              <Link
                to={`/apps/${app.name}?tab=logs`}
                onClick={(e) => e.stopPropagation()}
              >
                <Terminal className="h-3 w-3 mr-1" />
                Журналы
              </Link>
            </Button>
            {app.argocdUrl && (
              <Button asChild size="sm" variant="ghost" className="h-6 px-1.5 text-[10px]">
                <a
                  href={app.argocdUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={(e) => e.stopPropagation()}
                >
                  <ExternalLink className="h-3 w-3 mr-1" />
                  ArgoCD
                </a>
              </Button>
            )}
          </div>
        </CardContent>
      </Link>
    </Card>
  );
}
